import { el } from '../core/dom.js';
import { withProduct } from '../core/route-map.js';
import { icon } from './icons.js';

function initial(name) {
  const text = String(name || '?').trim();
  return text ? text.charAt(0).toUpperCase() : '?';
}

/** @param {unknown} raw */
function platformsOf(raw) {
  if (Array.isArray(raw)) return raw.map((item) => String(item || '').trim()).filter(Boolean);
  if (typeof raw === 'string' && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed.map((item) => String(item || '').trim()).filter(Boolean);
      }
    } catch { /* plain string */ }
    return [raw.trim()];
  }
  return [];
}

/** Prefer platform labels; fall back to slug so same-named products stay distinct. */
function productSecondary(item) {
  const platforms = platformsOf(item && item.platforms);
  if (platforms.length) return platforms.join(', ');
  return String((item && item.slug) || '').trim();
}

/** Drop duplicate ids (API bug); keep every distinct product. */
function uniqueById(products) {
  const seen = new Set();
  const out = [];
  for (const item of products || []) {
    if (!item || !item.id || seen.has(item.id)) continue;
    seen.add(item.id);
    out.push(item);
  }
  return out;
}

function nameDupes(products) {
  const counts = new Map();
  for (const item of products) {
    const key = String(item.name || 'Product').trim().toLowerCase();
    counts.set(key, (counts.get(key) || 0) + 1);
  }
  return counts;
}

/**
 * @param {object} state
 * @param {object} actions
 */
export function renderTopbar(state, actions) {
  const product = state.product;
  const name = product ? product.name : 'Select product';
  const first = !!state.firstProduct;
  return el('header', { class: first ? 'topbar is-first' : 'topbar' },
    state.hideNavToggle ? null : el('button', {
      type: 'button',
      class: 'icon-btn nav-toggle',
      'aria-label': 'Open menu',
      'aria-expanded': state.menuOpen ? 'true' : 'false',
      onClick: actions.onMenu,
    }, icon('menu')),
    first
      ? el('span', { class: 'topbar-brand' },
        el('img', {
          class: 'topbar-mark',
          src: '/site/assets/bonnie-mark-sm.png',
          alt: '',
          width: '28',
          height: '28',
        }),
        el('span', { class: 'topbar-word' }, 'Bonnie ', el('em', {}, 'Ai')))
      : productMenu(name, state, actions),
    el('span', { class: 'crumb-slash', 'aria-hidden': 'true' }, '/'),
    el('span', { class: 'crumb' }, state.crumb || 'Dashboard'),
    state.welcomeBadge
      ? el('span', { class: 'np-welcome-badge' }, '✦ Welcome, first product')
      : null,
    first ? el('span', { class: 'topbar-spacer' }) : el('button', {
      type: 'button',
      class: 'search-launch',
      onClick: actions.onSearch,
    },
    icon('search', 15),
    el('span', { class: 'search-placeholder' }, 'Search or ask Bonnie'),
    el('span', { class: 'kbd' }, '⌘K')),
    first ? null : el('button', {
      type: 'button',
      class: 'icon-btn search-icon-btn',
      'aria-label': 'Search',
      onClick: actions.onSearch,
    }, icon('search', 17)),
    el('button', {
      type: 'button',
      class: 'icon-btn',
      'aria-label': state.theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode',
      onClick: actions.onTheme,
    }, icon(state.theme === 'light' ? 'moon' : 'sun', 17)),
    first ? null : el('button', {
      type: 'button',
      class: 'icon-btn bell-btn',
      'aria-label': 'Notifications',
      onClick: actions.onNotifications,
    },
    icon('bell', 17),
    state.unread ? el('span', { class: 'bell-dot', 'aria-hidden': 'true' }) : null),
    userMenu(state, actions));
}

function productMenu(name, state, actions) {
  const products = uniqueById(state.products);
  const dupes = nameDupes(products);
  return el('div', { class: 'product-menu' },
    el('button', {
      type: 'button',
      class: 'product-trigger',
      id: 'product-trigger',
      'aria-haspopup': 'listbox',
      'aria-expanded': state.productsOpen ? 'true' : 'false',
      'aria-controls': state.productsOpen ? 'product-list' : null,
      onClick: actions.onToggleProducts,
    },
    el('span', { class: 'product-initial', 'aria-hidden': 'true' }, initial(name)),
    el('span', { class: 'product-name' }, name),
    icon('chevron', 14)),
    state.productsOpen ? el('div', {
      class: 'menu product-list',
      id: 'product-list',
      role: 'listbox',
      'aria-label': 'Products',
    },
      products.length
        ? products.map((item) => {
          const label = item.name || 'Product';
          const key = label.trim().toLowerCase();
          const secondary = dupes.get(key) > 1 ? productSecondary(item) : '';
          const kids = secondary
            ? [
              el('span', { class: 'menu-item-label' }, label),
              el('span', { class: 'menu-item-sub' }, secondary),
            ]
            : [label];
          return el('button', {
            type: 'button',
            class: item.id === state.productId
              ? (secondary ? 'menu-item is-on is-stack' : 'menu-item is-on')
              : (secondary ? 'menu-item is-stack' : 'menu-item'),
            role: 'option',
            'aria-selected': item.id === state.productId ? 'true' : 'false',
            'aria-label': secondary ? `${label} (${secondary})` : label,
            onClick: () => actions.onProduct(item.id),
          }, ...kids);
        })
        : el('p', { class: 'menu-empty' }, 'No products yet'),
      el('a', { class: 'menu-item', href: '/app/products/new' }, 'New product')) : null);
}

function userMenu(state, actions) {
  const person = state.me || {};
  const role = String(person.role || 'member').toUpperCase();
  const org = String(person.tenant_slug || person.tenant_name || '').toUpperCase();
  return el('div', { class: 'acct' },
    el('button', {
      type: 'button',
      class: 'avatar-btn',
      'aria-label': `Account — ${person.name || 'You'}`,
      'aria-haspopup': 'menu',
      'aria-expanded': state.accountOpen ? 'true' : 'false',
      onClick: actions.onToggleAccount,
    },
    initial(person.name),
    el('span', { class: 'avatar-dot', 'aria-hidden': 'true' })),
    state.accountOpen ? el('div', { class: 'menu account-menu', role: 'menu', 'aria-label': 'Account' },
      el('div', { class: 'account-card' },
        el('span', { class: 'avatar-btn avatar-sm', 'aria-hidden': 'true' }, initial(person.name)),
        el('span', {},
          el('span', { class: 'account-name' }, person.name || 'You'),
          el('span', { class: 'account-meta' }, `${role}${org ? ` · ${org}` : ''}`))),
      el('a', { class: 'menu-item', role: 'menuitem', href: withProduct('/app/settings', state.productId) }, icon('sliders', 15), 'Settings'),
      el('button', { type: 'button', class: 'menu-item', role: 'menuitem', onClick: actions.onTheme },
        icon(state.theme === 'light' ? 'moon' : 'sun', 15),
        state.theme === 'light' ? 'Dark mode' : 'Light mode'),
      el('button', { type: 'button', class: 'menu-item menu-out', role: 'menuitem', onClick: actions.onLogout },
        icon('logout', 15), 'Log out')) : null);
}
